import styled, { css, keyframes } from 'styled-components';
import { mq } from '../../../theme/mediaQueries';
import { StyledMultiSelectContent } from '../../atoms/Fields/MultiSelect/styles';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const StyledLayoutBaseWrapper = styled.div`
  min-height: 100vh;
  background: var(--paper);
  display: flex;

  /* Current in-flow sidebar width, exposed so viewport-fixed descendants
     (e.g. the OrderApprovalBar) can align to the content area instead of
     spanning under the sidebar. 0 on mobile where the sidebar is off-canvas. */
  --app-sidebar-w: var(--sidebar-w);
  &[data-sidebar-collapsed='true'] {
    --app-sidebar-w: var(--sidebar-w-collapsed);
  }
  ${mq.ltMd} {
    --app-sidebar-w: 0px;
  }

  &::after {
    content: '';
    min-height: inherit;
    font-size: 0;
  }
`;

export const StyledLayoutBaseSidebar = styled.div<{
  collapsed?: boolean;
  mobileOpen?: boolean;
}>`
  width: ${({ collapsed }) =>
    collapsed ? 'var(--sidebar-w-collapsed)' : 'var(--sidebar-w)'};
  background: var(--brand-ink);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 12;
  border-inline-end: 1px solid var(--brand-ink-3);
  transition: width var(--dur-slow) var(--ease-out);

  ${mq.ltMd} {
    position: fixed;
    inset: 0 auto 0 0;
    z-index: 200;
    width: var(--sidebar-w) !important;
    transform: ${({ mobileOpen }) =>
      mobileOpen ? 'translateX(0)' : 'translateX(-110%)'};
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1),
      box-shadow 0.28s ease;
    box-shadow: ${({ mobileOpen }) =>
      mobileOpen ? '4px 0 32px rgba(9,15,56,0.5)' : 'none'};

    [dir="rtl"] & {
      inset: 0 0 0 auto;
      transform: ${({ mobileOpen }) =>
        mobileOpen ? 'translateX(0)' : 'translateX(110%)'};
      box-shadow: ${({ mobileOpen }) =>
        mobileOpen ? '-4px 0 32px rgba(9,15,56,0.5)' : 'none'};
    }
  }
`;

export const StyledMobileBackdrop = styled.div<{ visible: boolean }>`
  display: none;

  ${mq.ltMd} {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(9, 15, 56, 0.55);
    z-index: 199;
    opacity: ${({ visible }) => (visible ? 1 : 0)};
    pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
    transition: opacity 0.28s ease;
    backdrop-filter: blur(2px);
  }
`;

export const StyledMobileMenuButton = styled.button`
  display: none;

  ${mq.ltMd} {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 2.25rem;
    height: 2.25rem;
    border: none;
    background: transparent;
    color: var(--ink);
    cursor: pointer;
    border-radius: var(--r-sm);
    padding: 0;
    transition: background var(--dur-base) var(--ease-out);

    &:hover { background: rgba(9, 15, 56, 0.06); }

    svg { width: 1.25rem; height: 1.25rem; }
  }
`;

export const StyledLayoutBaseDropdown = styled.div<{ collapsed?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
  ${({ collapsed }) => collapsed && css`display: none;`}
`;

export const StyledSidebarIdentityArea = styled.div<{ collapsed?: boolean }>`
  width: 100%;
  padding: ${({ collapsed }) => (collapsed ? '12px 8px' : '12px')};

  ${StyledMultiSelectContent} {
    border-color: rgba(9, 15, 56, 0.12);
    box-shadow: 0 8px 28px -4px rgba(9, 15, 56, 0.16),
      0 2px 8px -2px rgba(9, 15, 56, 0.08);
    border-radius: 14px;

    & > div:first-child {
      border-bottom-color: rgba(9, 15, 56, 0.08);
      margin-bottom: 8px;
      padding-bottom: 6px;
    }
  }
`;

export const StyledSidebarIdentity = styled.button<{ collapsed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: ${({ collapsed }) => collapsed ? '8px 0' : '10px 12px'};
  border-radius: var(--r-lg);
  appearance: none;
  background: rgba(255,255,255,0.08);
  color: var(--surface);
  transition: background var(--dur-base) var(--ease-out);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.1);
  text-align: start;

  ${({ collapsed }) => collapsed && css`justify-content: center;`}

  &:hover {
    background: rgba(255,255,255,0.13);
    border-color: rgba(255,255,255,0.16);
  }

  > * { position: relative; z-index: 1; }
`;

export const StyledSidebarChevron = styled.span<{ isOpen?: boolean }>`
  margin-inline-start: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: rgba(255,255,255,0.4);
  transition: transform var(--dur-base) var(--ease-out);
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};

  svg { width: 14px; height: 14px; fill: currentColor; }
`;

export const StyledSidebarMonogram = styled.span`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: var(--r-sm);
  background: var(--brand-lime);
  color: var(--brand-ink);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  line-height: 1;
`;

export const StyledSidebarIdentityName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--surface);
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

export const StyledCollapseToggle = styled.button<{ collapsed?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  padding: 6px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  display: grid;
  place-items: center;
  flex: none;
  box-sizing: border-box;
  margin-inline-start: auto;
  transition: background var(--dur-fast) var(--ease-out),
    border-color var(--dur-fast) var(--ease-out),
    color var(--dur-fast) var(--ease-out);

  svg {
    width: 12px;
    height: 12px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.14);
    border-color: rgba(255, 255, 255, 0.24);
    color: rgba(255, 255, 255, 1);
  }

  ${({ collapsed }) =>
    collapsed &&
    css`
      margin: 8px auto 0;
      display: flex;
      align-items: center;
      justify-content: center;
    `}

  ${mq.ltMd} { display: none; }
`;

export const StyledSidebarLogoRow = styled.div<{ collapsed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${({ collapsed }) => (collapsed ? '12px 8px' : '12px 12px 12px 16px')};
  height: var(--topbar-h);
  box-sizing: border-box;
  border-bottom: 1px solid var(--brand-ink-3);
  flex-shrink: 0;
  ${({ collapsed }) => collapsed && css`justify-content: center;`}
`;

export const StyledSidebarSpacer = styled.div`
  flex: 1;
`;

export const StyledSidebarFooter = styled.div<{ collapsed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${({ collapsed }) => (collapsed ? '12px 8px' : '12px')};
  border-top: 1px solid var(--brand-ink-3);
  flex-shrink: 0;
`;

export const StyledUserMonogram = styled.div`
  width: 32px;
  height: 32px;
  border-radius: var(--r-full);
  background: var(--brand-ink-3);
  color: var(--surface);
  flex: none;
  display: grid;
  place-items: center;
  font-weight: 600;
  font-size: var(--t-sm);
  text-transform: uppercase;
  flex-shrink: 0;
`;

export const StyledUserFooterInfo = styled.div`
  flex: 1;
  min-width: 0;

  .name {
    font-weight: 600;
    font-size: var(--t-sm);
    color: var(--surface);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .role {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.5);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const StyledUserMoreBtn = styled.button`
  background: transparent;
  border: none;
  padding: var(--s-1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color var(--dur-fast) var(--ease-out),
    background var(--dur-fast) var(--ease-out);

  &:hover {
    color: rgba(255, 255, 255, 0.85);
    background: rgba(255, 255, 255, 0.06);
  }
`;

export const StyledPoweredByLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.35);
`;

export const StyledFooterLogoMini = styled.div<{ collapsed?: boolean }>`
  display: flex;
  align-items: center;
  opacity: 0.75;
  flex-shrink: 0;
`;

export const StyledLayoutBaseMain = styled.main`
  flex: 1;
  min-width: 0;
  max-height: 100vh;
  overflow: auto;
  display: flex;
  flex-direction: column;
  background: var(--paper);
`;

export const StyledLayoutBaseContent = styled.div`
  flex: 1;
  padding: var(--s-7, 28px);
  display: flex;
  flex-direction: column;
  animation: ${fadeUp} 0.28s var(--ease-out);
  overflow-x: clip;
  overflow-y: visible;

  ${mq.ltMd} {
    padding: var(--s-4);
  }

  ${mq.ltSm} {
    padding: var(--s-3);
  }
`;

export const StyledLayoutBaseContentInner = styled.div`
  max-width: var(--content-max);
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  flex: 1;
`;
